import client from 'client'
import type {
  GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse,
  GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetPathParams,
  GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryParams,
  GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet422,
} from '../../types/GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryKey = (
  asset_position_id: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetPathParams['asset_position_id'],
  params?: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryParams,
) => [{ url: '/data/asset_position/:asset_position_id/time_series', params: { asset_position_id: asset_position_id } }, ...(params ? [params] : [])] as const

export type GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryKey = ReturnType<
  typeof getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryKey
>

/**
 * @summary Get Asset Position Timeseries
 * {@link /data/asset_position/:asset_position_id/time_series}
 */
export async function getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet(
  asset_position_id: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetPathParams['asset_position_id'],
  params?: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse,
    ResponseErrorConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet422>,
    unknown
  >({ method: 'GET', url: `/data/asset_position/${asset_position_id}/time_series`, params, ...requestConfig })
  return res
}

export function getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryOptions(
  asset_position_id: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetPathParams['asset_position_id'],
  params?: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryKey(asset_position_id, params)
  return queryOptions<
    ResponseConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse>,
    ResponseErrorConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet422>,
    ResponseConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse>,
    typeof queryKey
  >({
    enabled: !!asset_position_id,
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet(asset_position_id, params, config)
    },
  })
}

/**
 * @summary Get Asset Position Timeseries
 * {@link /data/asset_position/:asset_position_id/time_series}
 */
export function useGetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet<
  TData = ResponseConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse>,
  TQueryData = ResponseConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse>,
  TQueryKey extends QueryKey = GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryKey,
>(
  asset_position_id: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetPathParams['asset_position_id'],
  params?: GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryResponse>,
        ResponseErrorConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryKey(asset_position_id, params)

  const query = useQuery({
    ...(getAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGetQueryOptions(
      asset_position_id,
      params,
      config,
    ) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetAssetPositionTimeseriesDataAssetPositionAssetPositionIdTimeSeriesGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}